import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import './VendorAnalytics.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const VendorAnalytics = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('month');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, [timeframe]);

  const fetchAnalytics = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/analytics/vendor`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await res.json();
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.message || 'Failed to load analytics');
      }
    } catch (err) {
      console.error('Analytics error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading-spinner">Loading analytics...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!data) return <div className="error-message">No analytics data available</div>;

  const maxValue = data.monthlyData?.values ? Math.max(...data.monthlyData.values) : 100000;

  return (
    <div className="vendor-analytics-page">
      <div className="page-header">
        <h1><i className="fas fa-chart-line"></i> Analytics</h1>
        <p>Track your performance and earnings</p>
        <div className="header-actions">
          <select className="timeframe-select" value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 90 Days</option>
            <option value="year">Last 12 Months</option>
          </select>
          <Button variant="outline" icon="fa-download" onClick={() => alert('Export feature coming soon')}>
            Export Report
          </Button>
          <Button variant="secondary" onClick={() => navigate('/service-provider/dashboard')} icon="fa-arrow-left">
            Back
          </Button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Earnings</div>
          <div className="stat-value">${data.totalEarnings?.toLocaleString() || 0}</div>
          <div className="stat-trend up">from all completed jobs</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Jobs Completed</div>
          <div className="stat-value">{data.completedJobs || 0}</div>
          <div className="stat-trend up">total contracts</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Jobs</div>
          <div className="stat-value">{data.activeJobs || 0}</div>
          <div className="stat-trend">in progress</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Average Job Value</div>
          <div className="stat-value">${data.avgJobValue?.toLocaleString() || 0}</div>
          <div className="stat-trend">per contract</div>
        </div>
      </div>

      <div className="chart-card">
        <div className="chart-header">
          <h3>Monthly Earnings Trend</h3>
        </div>
        <div className="bar-chart">
          {data.monthlyData?.labels.map((label, idx) => (
            <div key={idx} className="bar-group">
              <div className="bar-container">
                <div className="bar" style={{ height: `${(data.monthlyData.values[idx] / maxValue) * 180}px` }}></div>
              </div>
              <span className="bar-label">{label}</span>
              <span className="bar-value">${(data.monthlyData.values[idx] / 1000).toFixed(0)}k</span>
            </div>
          ))}
        </div>
      </div>

      <div className="analytics-grid">
        <div className="performance-card">
          <h3><i className="fas fa-chart-simple"></i> Performance Metrics</h3>
          <div className="metric-item">
            <div className="metric-header"><span>Completion Rate</span><span>{data.completionRate || 85}%</span></div>
            <div className="metric-bar"><div className="metric-fill" style={{ width: `${data.completionRate || 85}%` }}></div></div>
          </div>
          <div className="metric-item">
            <div className="metric-header"><span>On-Time Delivery</span><span>{data.onTimeRate || 90}%</span></div>
            <div className="metric-bar"><div className="metric-fill" style={{ width: `${data.onTimeRate || 90}%` }}></div></div>
          </div>
          <div className="metric-item">
            <div className="metric-header"><span>Client Rating</span><span>{data.rating || '4.5'} ★</span></div>
            <div className="metric-bar"><div className="metric-fill" style={{ width: `${(data.rating / 5) * 100}%` }}></div></div>
          </div>
        </div>

        <div className="top-jobs-card">
          <h3><i className="fas fa-chart-pie"></i> Top Services</h3>
          {data.topServices?.length > 0 ? (
            data.topServices.map((service, idx) => (
              <div key={idx} className="job-item">
                <div>
                  <div className="job-name">{service.name}</div>
                  <div className="job-count">{service.count} jobs</div>
                </div>
                <div className="job-revenue">${service.revenue?.toLocaleString()}</div>
              </div>
            ))
          ) : (
            <p className="no-data">No service data available</p>
          )}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Quote Win Rate</div>
          <div className="stat-value">{data.winRate || 0}%</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Quotes Submitted</div>
          <div className="stat-value">{data.totalQuotes || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Quotes Accepted</div>
          <div className="stat-value">{data.acceptedQuotes || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Response Time</div>
          <div className="stat-value">{data.avgResponseTime || '24h'}</div>
        </div>
      </div>
    </div>
  );
};

export default VendorAnalytics;